import { useState, useEffect, useRef } from "react";

/**
 * WatchlistEditModal
 *
 * A modal that displays the user's watchlists with options to:
 *  - Create a new watchlist (text input + "+" button)
 *  - Delete a watchlist (trash icon; "Default" is protected)
 *
 * Props:
 *   isOpen        – boolean controlling visibility
 *   onClose       – callback to close the modal
 *   onListsChange – callback(updatedLists) fired after any create/delete
 *                   so the parent WatchlistPanel stays in sync
 */
export default function WatchlistEditModal({ isOpen, onClose, onListsChange }) {
  const [watchlists, setWatchlists] = useState([]);
  const [newName, setNewName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const API_URL =
    process.env.REACT_APP_API_URL ||
    (process.env.NODE_ENV === "development" ? "http://127.0.0.1:8000" : "");

  const getUserEmail = () => {
    let email = localStorage.getItem("userEmail");
    if (!email) {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split(".")[1]));
          if (payload.email) {
            email = payload.email;
            localStorage.setItem("userEmail", email);
          }
        } catch (e) {
          /* ignore */
        }
      }
    }
    return email;
  };

  // ── Fetch watchlists whenever modal opens ──────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    fetchWatchlists();
    // Auto-focus the input when opening
    setTimeout(() => inputRef.current?.focus(), 150);
  }, [isOpen]);

  const fetchWatchlists = async () => {
    const email = getUserEmail();
    if (!email) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_URL}/watchlists?email=${encodeURIComponent(email)}`
      );
      if (!res.ok) throw new Error("Failed to fetch watchlists");
      const data = await res.json();
      setWatchlists(data.watchlists || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Create ─────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    const email = getUserEmail();
    if (!email) return;

    setError(null);
    try {
      const res = await fetch(`${API_URL}/watchlists/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, wl_name: name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Create failed");

      setWatchlists(data.watchlists || []);
      setNewName("");
      // Notify parent so the dropdown updates
      onListsChange?.(data.watchlists.map((w) => w.wl_name));
      inputRef.current?.focus();
    } catch (err) {
      setError(err.message);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────
  const handleDelete = async (wlName) => {
    const email = getUserEmail();
    if (!email) return;

    setError(null);
    try {
      const res = await fetch(`${API_URL}/watchlists/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, wl_name: wlName }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Delete failed");

      setWatchlists(data.watchlists || []);
      // Notify parent
      onListsChange?.(data.watchlists.map((w) => w.wl_name));
    } catch (err) {
      setError(err.message);
    }
  };

  // ── Enter key support ──────────────────────────────────────────────────
  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleCreate();
  };

  if (!isOpen) return null;

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Modal */}
      <div
        className="bg-gray-800 border border-gray-600 rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-gray-100">
            Manage Watchlists
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-xl leading-none transition-colors"
            title="Close"
          >
            ✕
          </button>
        </div>

        {/* Create new — top */}
        <div className="px-5 py-4 border-b border-gray-700 bg-gray-800/80">
          <label className="block text-xs text-gray-400 mb-1.5 font-medium">
            Watchlist Name
          </label>
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g. Tech Stocks"
              maxLength={50}
              className="flex-1 bg-gray-700 text-gray-100 text-sm border border-gray-600 rounded-lg px-3 py-2 placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-colors"
            />
            <button
              onClick={handleCreate}
              disabled={!newName.trim()}
              className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold px-3.5 py-2 rounded-lg text-lg leading-none transition-colors"
              title="Create watchlist"
            >
              +
            </button>
          </div>
        </div>

        {/* Watchlist list */}
        <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
          {/* Error banner */}
          {error && (
            <div className="mb-3 text-sm text-red-400 bg-red-900/30 border border-red-700/50 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-gray-400 text-sm py-6 text-center">
              Loading…
            </div>
          ) : watchlists.length === 0 ? (
            <div className="text-gray-500 text-sm py-6 text-center">
              No watchlists found
            </div>
          ) : (
            <ul className="divide-y divide-gray-700/50">
              {watchlists.map((wl) => (
                <li
                  key={wl.wl_name}
                  className="flex items-center justify-between px-3 py-2.5 hover:bg-gray-700/50 transition-colors group"
                >
                  <span className="text-gray-200 text-sm font-medium truncate">
                    {wl.wl_name}
                    {wl.wl_name === "Default" && (
                      <span className="ml-2 text-[10px] text-gray-500 font-normal">
                        (protected)
                      </span>
                    )}
                  </span>

                  {wl.wl_name !== "Default" && (
                    <button
                      onClick={() => handleDelete(wl.wl_name)}
                      className="flex-shrink-0 ml-2 w-5 h-5 rounded-full bg-red-500 hover:bg-red-400 flex items-center justify-center transition-colors shadow-sm"
                      title={`Delete ${wl.wl_name}`}
                    >
                      {/* Minus sign */}
                      <span className="text-white text-xs font-bold leading-none">−</span>
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
